import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showAlert } from 'actions/app';
import axios from 'axios';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { useLatestRequestSignal } from 'hooks/useLatestRequestSignal';
import debounce from 'lodash/debounce';
import { useUserContext } from 'providers/UserProvider';
import strings from 'strings';
import { FormSchema } from 'UI/constants/entityTypes';
import { getErrorMessage } from 'UI/utils';
import { cleanCDSLocations } from 'utils/clean-cds-locations';
import { removeNullDeep } from 'utils/removeNullDeep';

import {
  saveCandidateDataSheet,
  saveCandidateDataSheetDraft
} from '../CandidateDataSheet.services';
import {
  CANDIDATE_DATA_SHEET_FULL_QUESTIONS_PATHS,
  CANDIDATE_DATA_SHEET_FULL_QUESTIONS_PATHS_REQUIRED
} from '../CandidateDataSheetDrawer.constants';
import { formatDataSheetDataForAPI, getDataSheetCount } from '../CandidateDataSheetDrawer.utils';

import useCandidateDataSheetSchema from './useCandidateDataSheetSchema';
import useFetchCandidateDataSheetData from './useFetchCandidateDataSheetData';

const { uiMessages, title } = strings.candidates.editDataSheet;

const MILLISECONDS_TO_WAIT = 2000;

/**
 * @param {Object} props
 * @param {string} props.candidateDataSheetId
 * @param {Function} props.onSaveCandidateDataSheet
 * @param {Function} props.onClose
 */
const useCandidateDataSheet = ({ candidateDataSheetId, onSaveCandidateDataSheet, onClose }) => {
  const draftCandidateDataSheetId = useRef(null);
  const saveButtonRef = useRef(null);
  const [user] = useUserContext();
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const formDataRef = useRef(null);
  const { nextSignal: nextDraftSignal, abort: abortDraft } = useLatestRequestSignal({
    abortOnUnmount: false
  });
  const isFinalSaveInProgress = useRef(false);
  const latestSaveRequestId = useRef(0);
  const pendingDraftAfterFinalSave = useRef(null);

  const {
    data: candidateDataSheetData,
    error: errorData,
    isFetching: isFetchingCandidateDataSheetData
  } = useFetchCandidateDataSheetData({
    candidateDataSheetId: formDataRef.current ? '' : candidateDataSheetId
  });
  const [schemaState] = useCandidateDataSheetSchema();

  useEffect(() => {
    if (!errorData && candidateDataSheetData?.schema.key) {
      formDataRef.current = removeNullDeep(candidateDataSheetData.data);
    }
  }, [candidateDataSheetData, errorData]);

  const onSaveClick = () => {
    if (!saveButtonRef.current) return;
    saveButtonRef.current.click();
  };

  const performSaveDataSheet = useCallback(
    async (
      form,
      { shouldDisplayLoading = true, isDraft = false, shouldClose = true },
      config,
      requestId
    ) => {
      const { formData } = form;
      formDataRef.current = formData;

      const schemaName = FormSchema.CandidateDataSheetFull;

      const progress = getDataSheetCount(
        formData,
        CANDIDATE_DATA_SHEET_FULL_QUESTIONS_PATHS,
        schemaName
      );
      const sanitizedFormDataWithCleanedLocations = isDraft
        ? formData
        : cleanCDSLocations(formData);

      const finalCandidateDataSheetId = draftCandidateDataSheetId.current || candidateDataSheetId;
      const data = formatDataSheetDataForAPI({
        id: finalCandidateDataSheetId,
        formData: sanitizedFormDataWithCleanedLocations,
        schemaName,
        user,
        progress,
        schemaVersion: schemaState.data.version
      });

      try {
        shouldDisplayLoading && setIsSaving(true);

        const response = await (isDraft ? saveCandidateDataSheetDraft : saveCandidateDataSheet)(
          data,
          config
        );

        if (requestId !== latestSaveRequestId.current) return false;

        if ([HTTPStatusCodes.Ok, HTTPStatusCodes.Created].includes(response.status)) {
          if (response.data.id) {
            const progressWithRequiredQuestions = getDataSheetCount(
              formData,
              CANDIDATE_DATA_SHEET_FULL_QUESTIONS_PATHS_REQUIRED,
              schemaName
            );
            draftCandidateDataSheetId.current = response.data.id;
            onSaveCandidateDataSheet(response.data.id, progressWithRequiredQuestions);
          }

          const finalMessage =
            progress.answered === progress.total
              ? uiMessages.complete.success
              : uiMessages.draft.success;

          shouldDisplayLoading &&
            dispatch(
              showAlert({
                severity: 'success',
                title: 'Success',
                body: finalMessage
              })
            );

          shouldDisplayLoading && setIsSaving(false);
          shouldClose && onClose();

          return true;
        }

        const errorMessage =
          progress.answered === progress.total ? uiMessages.complete.error : uiMessages.draft.error;

        shouldDisplayLoading &&
          dispatch(
            showAlert({
              severity: 'error',
              title,
              body: errorMessage
            })
          );

        shouldDisplayLoading && setIsSaving(false);
        return false;
      } catch (error) {
        if (shouldDisplayLoading && !axios.isCancel(error)) {
          dispatch(
            showAlert({
              severity: 'error',
              title,
              body: getErrorMessage(error)
            })
          );
        }

        shouldDisplayLoading && setIsSaving(false);
        return false;
      }
    },
    [candidateDataSheetId, dispatch, schemaState, onSaveCandidateDataSheet, user, onClose]
  );

  const saveDraftDataSheet = useCallback(
    (form, options) => {
      if (isFinalSaveInProgress.current) {
        pendingDraftAfterFinalSave.current = { form, options };
        return Promise.resolve(false);
      }

      const signal = nextDraftSignal();
      latestSaveRequestId.current += 1;

      return performSaveDataSheet(form, options, { signal }, latestSaveRequestId.current);
    },
    [nextDraftSignal, performSaveDataSheet]
  );

  const debouncedSaveDataSheet = useMemo(
    () => debounce(saveDraftDataSheet, MILLISECONDS_TO_WAIT),
    [saveDraftDataSheet]
  );

  const handleSaveDataSheet = useCallback(
    async (form, options = {}) => {
      if (options.isDraft) {
        return saveDraftDataSheet(form, options);
      }

      if (isFinalSaveInProgress.current) return undefined;

      isFinalSaveInProgress.current = true;
      debouncedSaveDataSheet.flush();
      abortDraft();

      let wasSaved = false;
      latestSaveRequestId.current += 1;

      try {
        wasSaved = await performSaveDataSheet(form, options, {}, latestSaveRequestId.current);
        return wasSaved;
      } finally {
        isFinalSaveInProgress.current = false;
        const pendingDraft = pendingDraftAfterFinalSave.current;
        pendingDraftAfterFinalSave.current = null;

        if (!wasSaved && pendingDraft) {
          saveDraftDataSheet(pendingDraft.form, pendingDraft.options);
        }
      }
    },
    [abortDraft, debouncedSaveDataSheet, performSaveDataSheet, saveDraftDataSheet]
  );

  useEffect(() => {
    return () => {
      debouncedSaveDataSheet.flush();
    };
  }, [debouncedSaveDataSheet]);

  const handleOnChange = useCallback(
    form => {
      formDataRef.current = form.formData;
      const options = {
        shouldDisplayLoading: false,
        isDraft: true,
        shouldClose: false
      };

      if (isFinalSaveInProgress.current) {
        pendingDraftAfterFinalSave.current = { form, options };
        return;
      }

      debouncedSaveDataSheet(form, options);
    },
    [debouncedSaveDataSheet]
  );

  return [
    {
      saveButtonRef,
      isSaving,
      formData: formDataRef.current,
      candidateDataSheetData,
      isFetchingCandidateDataSheetData,
      errorCandidateDataSheetData: errorData,
      primaryButtonName: candidateDataSheetId ? strings.shared.ui.update : strings.shared.ui.save,
      ...schemaState
    },
    {
      onSaveClick,
      save: handleSaveDataSheet,
      handleOnChange
    }
  ];
};

export default useCandidateDataSheet;
